/**
 * 💸 지출 문서 송금완료 표시 (v2.267.0 · 2026-10-15 · 개발 계획 #19)
 *
 * 사용자: 「대표이사까지 결재가 다 완료된 다음에, 해당 지출요청 건이 송금이 되었는지를 경영지원부에서 표시할 수 있게.
 *   송금완료 체크박스로 해도 되고. 체크하면 송금일을 적게 해주고, 기본은 체크한 당일로 날짜가 입력돼 있고 수정할 수도 있게」.
 *
 * 문서 목록의 체크칸과 문서 상세 창이 같이 쓴다 — 체크를 누르면 이 창이 뜨고, 송금일(기본 오늘)을 확인해 저장한다.
 * 이미 송금완료인 문서는 날짜를 고치거나 체크를 풀 수 있다.
 * ⭐권한(경영지원 지정 계정·admin)·「최종승인만」·「오늘까지」는 서버(PATCH /expense/docs/:id/remit)가 판정한다 — 화면은 단추만 감춘다.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DraggableModal } from './DraggableModal';
import { DateInput } from './DateInput';
import * as api from '../lib/expense-api';
import { todayKst } from '../lib/date-label';

export interface RemitTarget {
  id: number;
  docNo: string;
  docType: string;
  authorName: string;
  /** 지금 송금일 — null = 아직 송금 전 */
  remitDate: string | null;
}

/**
 * 지급일이 지났나·오늘인가 (v2.268.0 · 재검토 B) — 송금 대기 문서만 의미가 있다. 날짜 비교는 KST 오늘 기준.
 */
export function payDueOf(payDate: string | null | undefined, today = todayKst()): 'overdue' | 'today' | null {
  if (!payDate || !/^\d{4}-\d{2}-\d{2}$/.test(payDate)) return null;
  return payDate < today ? 'overdue' : payDate === today ? 'today' : null;
}

/**
 * 💸 송금 상태 글 한 벌 (v2.268.0) — 목록 칸(compact)과 문서 상세 창이 같이 쓴다.
 * ⭐상태는 서버가 정한 remitState 만 본다(status·remitDate 로 다시 판정하지 않는다 — v2.267.0 은 네 곳에 흩어져 있었다).
 */
export function RemitStateLabel({ state, remitDate, remitByName, payDate, compact = false }: {
  state: api.RemitState | undefined;
  remitDate?: string | null;
  remitByName?: string | null;
  /** 작성자 지급일(날짜 칸) — 송금 대기일 때 「지급일 지남·오늘 지급」 */
  payDate?: string | null;
  compact?: boolean;
}) {
  if (!state || state === 'none') return <span className="text-slate-300">-</span>;
  if (state === 'waiting') {
    return (
      <span className="text-slate-400" title="대표이사 결재까지 끝나면 경영지원에서 송금완료를 표시합니다">
        {compact ? '결재 중' : '결재 끝난 뒤 표시'}
      </span>
    );
  }
  if (state === 'done') {
    return (
      <span className="text-emerald-700 font-semibold" title={`송금일 ${remitDate ?? ''}${remitByName ? ` · ${remitByName}` : ''}`}>
        ✅ {compact ? (remitDate ?? '').slice(5) : `송금완료 ${remitDate ?? ''}`}
        {!compact && remitByName && <span className="text-xs text-slate-500 font-normal"> · {remitByName}</span>}
      </span>
    );
  }
  if (state === 'legacy') {
    return (
      <span className="text-slate-400" title="송금완료 표시 기능(v2.267.0) 전에 결재가 끝난 문서 — 송금 대기로 세지 않습니다">
        이전 문서
      </span>
    );
  }
  const due = payDueOf(payDate);
  return (
    <span className={due === 'overdue' ? 'text-rose-700 font-semibold' : 'text-amber-700 font-semibold'}
      title={due === 'overdue' ? `지급일(${payDate})이 지났는데 아직 송금완료 표시가 없습니다` : due === 'today' ? '오늘이 지급일입니다' : undefined}>
      {/* 목록 칸(compact)은 「지남·오늘」을 아랫줄 지급일 옆에 둔다 — 여기 붙이면 1280 에서 표가 11px 넘쳤다(v2.268.0 실측) */}
      ⏳ {compact ? '대기' : '송금 대기'}{!compact && (due === 'overdue' ? ' · 지급일 지남' : due === 'today' ? ' · 오늘 지급' : '')}
    </span>
  );
}

interface Props {
  target: RemitTarget;
  onClose: () => void;
  /** 저장에 성공했다 — 알림 한 줄 */
  onDone?: (message: string) => void;
}

export function ExpenseRemitModal({ target, onClose, onDone }: Props) {
  const qc = useQueryClient();
  const already = target.remitDate !== null;
  // 체크칸을 눌러 열었다 = 「송금완료로」 — 이미 완료인 문서는 체크된 채로 열고 풀 수도 있게
  const [checked, setChecked] = useState(true);
  const [date, setDate] = useState<string>(target.remitDate ?? todayKst());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const today = todayKst();
  const unchanged = already ? checked && date === target.remitDate : !checked;

  async function save() {
    if (busy || unchanged) return;
    if (checked) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setErr('송금일을 넣어 주세요'); return; }
      if (date > today) { setErr('송금일은 오늘보다 뒤일 수 없습니다 — 송금을 마친 뒤에 표시해 주세요'); return; }
    }
    setBusy(true);
    setErr(null);
    try {
      await api.setExpenseRemit(target.id, checked ? date : null);
      void qc.invalidateQueries({ queryKey: ['expense'] });
      // ⚠「알림을 보냈다」고 적지 않는다 — 본인 문서면 안 가고, 봇 발송은 저장과 따로 돈다(실패해도 저장은 된다)
      onDone?.(checked
        ? already ? `✓ ${target.docNo} 송금일을 ${date} 로 고쳤습니다.` : `✓ ${target.docNo} 송금완료 (송금일 ${date})`
        : `✓ ${target.docNo} 송금완료 표시를 풀었습니다.`);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  // 창 높이 — 보이는 칸만큼(넘치면 창 안이 스크롤). 1280 실측: 250 이면 스크롤 막대가 생겼다
  const height = 290 + (err ? 56 : 0);

  return (
    <DraggableModal onClose={onClose} initialWidth={420} initialHeight={height} zIndex={80}
      closeOnBackdrop={false} closeOnEsc={false}>
      <div className="dm-drag-handle cursor-move flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <h3 className="ui-subsection-title">💸 송금완료 — {target.docNo}</h3>
        <button type="button" onClick={onClose} aria-label="닫기" className="ui-close">×</button>
      </div>
      <div className="p-4 space-y-3 text-sm">
        <div className="text-xs text-slate-500">{target.docType} · 작성 {target.authorName}</div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" className="w-4 h-4" checked={checked}
            onChange={(e) => { setChecked(e.target.checked); setErr(null); }} />
          <span className="font-semibold text-slate-800">송금완료</span>
        </label>
        {checked ? (
          <label className="block">
            <span className="text-xs font-medium text-slate-600">송금일 (기본 오늘 · 고칠 수 있습니다)</span>
            <DateInput value={date} onChange={setDate} max={today} required />
            {!already && (
              <span className="block text-xs text-slate-500 mt-1">처음 송금완료로 표시하면 작성자에게 웍스 알림이 갑니다(금액은 싣지 않습니다).</span>
            )}
          </label>
        ) : (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 leading-relaxed">
            {already
              ? <>송금완료 표시를 풉니다 — 송금일 {target.remitDate} 이 지워지고 「송금 대기」로 돌아갑니다.</>
              : <>체크하지 않으면 아무것도 바뀌지 않습니다.</>}
          </div>
        )}
        {err && (
          <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded p-2 whitespace-pre-line">{err}</div>
        )}
      </div>
      <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
        <button type="button" onClick={onClose} disabled={busy} className="btn btn-secondary">취소</button>
        <button type="button" onClick={() => void save()} disabled={busy || unchanged} className="btn btn-primary">
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>
    </DraggableModal>
  );
}
